import React, { useState, Fragment } from 'react';
import emailjs from '@emailjs/browser';
import { MailIcon, PhoneIcon, MapPinIcon, LoaderIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/translations';
// ⚠️ Replace these with your actual EmailJS credentials
const EMAILJS_SERVICE_ID = 'service_4s13abg';
const EMAILJS_TEMPLATE_ID = 'template_iqpfyvn';
const EMAILJS_PUBLIC_KEY = 'NpjOy8IRi51SAvHL4';
export function ContactSection() {
  const {
    language
  } = useLanguage();
  const t = translations[language];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [errors, setErrors] = useState<{
    [key: string]: string;
  }>({});
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const validateForm = () => {
    const newErrors: {
      [key: string]: string;
    } = {};
    if (!formData.name.trim()) {
      newErrors.name = t.contact.form.errors.nameRequired;
    }
    if (!formData.email.trim()) {
      newErrors.email = t.contact.form.errors.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t.contact.form.errors.emailInvalid;
    }
    if (!formData.message.trim()) {
      newErrors.message = t.contact.form.errors.messageRequired;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendError('');
    if (validateForm()) {
      setIsSending(true);
      try {
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message
        }, EMAILJS_PUBLIC_KEY);
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          message: ''
        });
        // Hide success message after 5 seconds
        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } catch (error) {
        console.error('EmailJS error:', error);
        setSendError(t.contact.form.errors.sendFailed);
      } finally {
        setIsSending(false);
      }
    }
  };
  return <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-id="element-19">
      <div className="max-w-7xl mx-auto" data-id="element-20">
        <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 text-center" data-id="element-21">
          {t.contact.title}
        </h2>
        <p className="text-xl text-gray-600 mb-12 text-center max-w-3xl mx-auto" data-id="element-22">
          {t.contact.subtitle}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-23">
          {/* Contact Form */}
          <div data-id="element-24">
            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-25">
              <div data-id="element-26">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-27">
                  {t.contact.form.name}
                </label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${errors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 hover:border-blue-400 hover:shadow-sm'}`} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'name-error' : undefined} data-id="element-28" />
                {errors.name && <p id="name-error" className="mt-1 text-sm text-red-600" role="alert" data-id="element-29">
                    {errors.name}
                  </p>}
              </div>

              <div data-id="element-30">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-31">
                  {t.contact.form.email}
                </label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${errors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 hover:border-blue-400 hover:shadow-sm'}`} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-error' : undefined} data-id="element-32" />
                {errors.email && <p id="email-error" className="mt-1 text-sm text-red-600" role="alert" data-id="element-33">
                    {errors.email}
                  </p>}
              </div>

              <div data-id="element-34">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-35">
                  {t.contact.form.message}
                </label>
                <textarea id="message" name="message" rows={6} value={formData.message} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 resize-none ${errors.message ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500 hover:border-blue-400 hover:shadow-sm'}`} aria-invalid={!!errors.message} aria-describedby={errors.message ? 'message-error' : undefined} data-id="element-36" />
                {errors.message && <p id="message-error" className="mt-1 text-sm text-red-600" role="alert" data-id="element-37">
                    {errors.message}
                  </p>}
              </div>

              <button type="submit" disabled={isSending} className={`w-full px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg shadow-md transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-blue-500/50 flex items-center justify-center gap-2 ${isSending ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-700 hover:scale-105 hover:shadow-lg'}`} data-id="element-38">
                {isSending ? <>
                    <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-39" />
                    {t.contact.form.sending}
                  </> : t.contact.form.submit}
              </button>

              {isSubmitted && <div className="p-4 bg-green-50 border border-green-200 rounded-lg" role="alert" data-id="element-40">
                  <p className="text-green-800 font-medium" data-id="element-41">
                    {t.contact.form.success}
                  </p>
                </div>}

              {sendError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg" role="alert" data-id="element-42">
                  <p className="text-red-800 font-medium" data-id="element-43">{sendError}</p>
                </div>}
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8" data-id="element-44">
            <div data-id="element-45">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-46">
                {t.contact.info.title}
              </h3>
              <p className="text-gray-600 mb-8" data-id="element-47">{t.contact.info.desc}</p>
            </div>

            <div className="space-y-6" data-id="element-48">
              <div className="flex items-start" data-id="element-49">
                <div className="flex-shrink-0" data-id="element-50">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg" data-id="element-51">
                    <MapPinIcon className="w-6 h-6 text-blue-600" data-id="element-52" />
                  </div>
                </div>
                <div className="ml-4" data-id="element-53">
                  <h4 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-54">
                    {t.contact.info.address.label}
                  </h4>
                  <p className="text-gray-600" data-id="element-55">{'AL16, Luanda, Angola'}</p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-56">
                <div className="flex-shrink-0" data-id="element-57">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg" data-id="element-58">
                    <PhoneIcon className="w-6 h-6 text-blue-600" data-id="element-59" />
                  </div>
                </div>
                <div className="ml-4" data-id="element-60">
                  <h4 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-61">
                    {t.contact.info.phone.label}
                  </h4>
                  <p className="text-gray-600" data-id="element-62">+244 923 322 185</p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-63">
                <div className="flex-shrink-0" data-id="element-64">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg" data-id="element-65">
                    <MailIcon className="w-6 h-6 text-blue-600" data-id="element-66" />
                  </div>
                </div>
                <div className="ml-4" data-id="element-67">
                  <h4 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-68">
                    {t.contact.info.email.label}
                  </h4>
                  <p className="text-gray-600" data-id="element-69">
                    info@crossroadsconsultinglda.com
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-gray-200" data-id="element-70">
              <h4 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-71">
                {t.contact.info.hours.title}
              </h4>
              <p className="text-gray-600" data-id="element-72">
                {t.contact.info.hours.weekdays}
                <br data-id="element-73" />
                {t.contact.info.hours.weekend}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}